import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  BookmarkBorder,
  Share,
  AccessTime,
  CheckCircle,
  SkipNext,
  SkipPrevious,
  Note,
  Close,
  Edit,
  Save,
} from '@mui/icons-material';
import useLectureVideo from '../../../hooks/leacture-detail/useLectureVideo';
import { formatTime } from '../../../utils/TimeUtils';
import Modal from '../../../components/common/Modal';

// Styled Components - 강의 사이트 스타일
const Card = styled.div`
  width: 100%;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
  border: 1px solid #e7e1d3;
  box-shadow: 6px 6px 0 #e9e2d0;
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #1d2340;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e3f2fd;
`;

const VideoText = styled.h6`
  color: #666;
  margin: 0;
  font-size: 18px;
  font-weight: 500;
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.85) 100%);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ProgressContainer = styled.div`
  flex-grow: 1;
  margin: 0 8px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.28);
  border-radius: 99px;
  overflow: hidden;
  cursor: pointer;
`;

const ProgressFill = styled.div`
  width: ${props => props.value}%;
  height: 100%;
  background: #ffe58a;
  border-radius: 99px;
`;

const TimeText = styled.span`
  color: white;
  font-size: 12px;
  min-width: 60px;
  text-align: center;
`;

const VolumeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const VolumeSlider = styled.input`
  width: 72px;
  height: 4px;
  accent-color: #ffe58a;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  border-radius: 2px;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 12px;
    height: 12px;
    background: #ffe58a;
    border-radius: 50%;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    background: #ffe58a;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const VideoNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const NavButton = styled.button`
  background: #fff;
  border: 1px solid #e7e1d3;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1d2340;

  &:hover:not(:disabled) {
    border-color: #1d2340;
    background: #f8f5ee;
  }

  &:disabled {
    background: #f8f5ee;
    color: #c4bba6;
    border-color: #e7e1d3;
    cursor: not-allowed;
  }
`;

const VideoIndex = styled.span`
  font-size: 14px;
  color: #666;
  margin: 0 8px;
`;

const VideoInfo = styled.div`
  padding: 22px 24px 24px;
  background: #fff;
  @media (max-width: 768px) { padding: 18px 16px 20px; }
`;

const VideoKicker = styled.p`
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #d9412b;
`;

const VideoTitle = styled.h2`
  margin: 0 0 14px;
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #1d2340;
  line-height: 1.35;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e7e1d3;
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #6f6a5c;
`;

const ActionButtons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #666;
  
  &:hover {
    background-color: rgba(29, 35, 64, 0.08);
    color: #1d2340;
  }
`;

const VideoDescription = styled.p`
  font-size: 15px;
  color: #6f6a5c;
  line-height: 1.7;
  margin: 0;
`;

const MemoToggleButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background: #1d2340;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.02em;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 6px 6px 0 rgba(29, 35, 64, 0.18);
  z-index: 1000;

  &:hover {
    background: #2c3561;
  }

  @media (max-width: 768px) {
    bottom: 16px;
    right: 16px;
  }
`;

const MemoPanel = styled.div`
  position: fixed;
  bottom: 76px;
  right: 24px;
  width: 420px;
  max-height: 560px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 6px 6px 0 #e9e2d0;
  border: 1px solid #e7e1d3;
  display: flex;
  flex-direction: column;
  z-index: 999;
  overflow: hidden;
  @media (max-width: 768px) {
    width: calc(100vw - 40px);
    right: 20px;
    bottom: 80px;
    max-height: calc(100vh - 120px);
  }
`;

const MemoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: #f8f5ee;
  border-bottom: 1px solid #e7e1d3;
  color: #1d2340;
`;

const MemoTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #1d2340;
`;

const MemoActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModeButton = styled.button`
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: ${props => props.active ? '#ffe58a' : '#fff'};
  color: #1d2340;
  border: 1px solid ${props => props.active ? '#ffe58a' : '#e7e1d3'};
`;

const SaveButton = styled.button`
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  background: #1d2340;
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #2c3561;
  }
`;

const CloseButton = styled.button`
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  color: #1d2340;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #fff;
  }
`;

const MemoContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const MemoTextarea = styled.textarea`
  width: 100%;
  flex: 1;
  min-height: 400px;
  padding: 20px;
  border: none;
  font-size: 15px;
  line-height: 1.8;
  font-family: inherit;
  background: #fffdf7;
  color: #1d2340;
  resize: none;
  &:focus {
    outline: none;
    background: #fff;
  }

  &::placeholder {
    color: #b3ac9c;
  }
`;

const MemoCanvasContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MemoToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e7e1d3;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ToolLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-right: 4px;
`;

const ToolButton = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active 
    ? '#1d2340' 
    : 'white'
  };
  color: ${props => props.active ? 'white' : '#64748b'};
  border: 2px solid ${props => props.active 
    ? 'transparent' 
    : 'rgba(29, 35, 64, 0.16)'
  };
  
  &:hover {
    background: ${props => props.active 
      ? '#1d2340' 
      : 'rgba(29, 35, 64, 0.08)'
    };
  }
`;

const SizeButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  border: 2px solid rgba(29, 35, 64, 0.16);
  color: #1d2340;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(29, 35, 64, 0.08);
    border-color: rgba(29, 35, 64, 0.28);
  }
`;

const SizeDisplay = styled.div`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  background: white;
  border: 2px solid rgba(29, 35, 64, 0.16);
  color: #1d2340;
  min-width: 40px;
  text-align: center;
`;

const MemoCanvasWrapper = styled.div`
  position: relative;
  flex: 1;
  background: #f8fafc;
  min-height: 300px;
`;

const MemoCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
`;

const LectureVideo = ({nav, onNavChangeHandler}) => {
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [ctx, setCtx] = useState();
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [eraserSize, setEraserSize] = useState(10);
  const [penSize, setPenSize] = useState(2);
  const [memoMode, setMemoMode] = useState('text'); // 'text' or 'canvas'

  const {
    isOpenModal,
    modalProps,
    setIsOpenModal,
    setModalProps,
    isPlaying,
    progress,
    currentTime,
    duration,
    volume,
    isMuted,
    isFullscreen,
    videoList,
    nowVideoIndex,
    videoRef,
    handlePlayPause,
    handleProgressClick,
    handleLoadedMetadata,
    handleTimeUpdate,
    handlePlay,
    handlePause,
    handleEnded,
    handleVolumeChange,
    handleMuteToggle,
    handleFullscreen,
    handleVideoChange,
    handleNextVideo,
    handlePrevVideo,
    isVideoLoaded,
    totalWatchTime,
    isWatching,
    isMemoOpen,
    setIsMemoOpen,
    memoText,
    setMemoText,
    memoCanvasData,
    setMemoCanvasData,
    saveMemo,
    fetchMemo,
  } = useLectureVideo(nav, onNavChangeHandler);


  // 캔버스 초기화
  useEffect(() => {
    if (!isMemoOpen || memoMode !== 'canvas') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const parent = canvas.parentElement;
    if (!parent) return;

    const resizeCanvas = () => {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;

      const context = canvas.getContext("2d");
      context.strokeStyle = "black";
      context.lineWidth = penSize;
      context.lineCap = "round";
      contextRef.current = context;
      setCtx(context);
    };

    resizeCanvas();
    const resizeObserver = new ResizeObserver(resizeCanvas);
    resizeObserver.observe(parent);

    return () => resizeObserver.disconnect();
  }, [isMemoOpen, memoMode, penSize]);

  // 메모 패널 열릴 때 메모 불러오기
  useEffect(() => {
    if (isMemoOpen) {
      fetchMemo();
    }
  }, [isMemoOpen, nowVideoIndex]);

  // 캔버스 데이터 불러오기
  useEffect(() => {
    if (memoCanvasData && ctx && canvasRef.current && memoMode === 'canvas') {
      const canvas = canvasRef.current;
      const context = ctx;
      
      const renderCanvas = () => {
        if (memoCanvasData && memoCanvasData.startsWith('data:image/')) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.onerror = () => {};
          img.src = memoCanvasData;
        }
      };

      requestAnimationFrame(renderCanvas);
    } else if (memoMode === 'canvas' && ctx && canvasRef.current && !memoCanvasData) {
      // 캔버스 데이터가 없으면 초기화
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [memoCanvasData, ctx, memoMode]);

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    setIsDrawing(true);
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
    }
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing || !ctx) return;
    const { offsetX, offsetY } = nativeEvent;
    ctx.lineTo(offsetX, offsetY);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const toggleEraser = () => {
    if (!ctx) return;
    setIsErasing(prev => {
      const next = !prev;
      ctx.globalCompositeOperation = next ? "destination-out" : "source-over";
      ctx.lineWidth = next ? eraserSize : penSize;
      return next;
    });
  };

  const UpToolSize = () => {
    const newSize = (isErasing ? eraserSize : penSize) + 1;
    if (isErasing) {
      setEraserSize(newSize);
    } else {
      setPenSize(newSize);
    }
    if (ctx) {
      ctx.lineWidth = newSize;
    }
  };

  const DownToolSize = () => {
    const newSize = Math.max(1, (isErasing ? eraserSize : penSize) - 1);
    if (isErasing) {
      setEraserSize(newSize);
    } else {
      setPenSize(newSize);
    }
    if (ctx) {
      ctx.lineWidth = newSize;
    }
  };

  const handleClearCanvas = () => {
    if (!ctx || !canvasRef.current) return;
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleSaveMemo = async () => {
    let canvasData = memoCanvasData; // 기존 캔버스 데이터 유지
    
    // 캔버스 모드일 때 현재 캔버스 상태를 저장
    if (memoMode === 'canvas' && ctx && canvasRef.current) {
      canvasData = canvasRef.current.toDataURL('image/png');
      setMemoCanvasData(canvasData);
    }
    
    // 텍스트와 캔버스 데이터를 모두 저장
    const result = await saveMemo(memoText, canvasData);
    
    if (result && result.success) {
      setModalProps({
        title: '성공',
        message: '메모가 저장되었습니다.',
        onConfirm: () => setIsOpenModal(false),
        confirmButtonName: '확인',
        cancelButtonName: null
      });
      setIsOpenModal(true);
      // 저장 후 메모 패널을 닫지 않음 (사용자가 계속 작업할 수 있도록)
    } else {
      setModalProps({
        title: '오류',
        message: '메모 저장에 실패했습니다: ' + (result?.error || '알 수 없는 오류'),
        onConfirm: () => setIsOpenModal(false),
        confirmButtonName: '확인',
        cancelButtonName: null
      });
      setIsOpenModal(true);
    }
  };

  if(!videoList.length){
    return null;
  }

  return (
    <>
      <Card>
      {/* 비디오 플레이어 */}
      <VideoContainer>
        {/* 실제 비디오 태그를 여기에 넣으세요 */}
        <video
          width="100%"
          height="100%"
          src={videoList[nowVideoIndex]?.video}
          style={{ background: '#1d2340', objectFit: 'contain', width: '100%', height: '100%' }}
          controls={false}
          ref={videoRef}
          onClick={handlePlayPause}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          preload="metadata"
        />

        {/* 비디오 컨트롤 */}
        <VideoControls>
          <ControlButton onClick={handlePlayPause}>
            {isPlaying ? <Pause /> : <PlayArrow />}
          </ControlButton>

          <ProgressContainer>
            <ProgressBar onClick={handleProgressClick}>
              <ProgressFill value={progress} />
            </ProgressBar>
          </ProgressContainer>

          <TimeText>
            {formatTime(currentTime)} / {isVideoLoaded ? formatTime(duration) : '로딩 중...'}
          </TimeText>

          <VolumeContainer>
            <ControlButton onClick={handleMuteToggle}>
              {isMuted ? <VolumeOff /> : <VolumeUp />}
            </ControlButton>
            <VolumeSlider
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
            />
          </VolumeContainer>

          <ControlButton onClick={handleFullscreen}>
            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
          </ControlButton>
        </VideoControls>
      </VideoContainer>
      <VideoInfo>
        <VideoKicker>강의</VideoKicker>
        <VideoTitle>
          {videoList[nowVideoIndex]?.title || '비디오 제목'}
        </VideoTitle>

        <StatusBar>
          <StatusItem>
            <AccessTime style={{ color: '#6f6a5c', fontSize: 18 }} />
            <StatusText>{isVideoLoaded ? formatTime(duration) : '길이 확인 중'}</StatusText>
          </StatusItem>
          <StatusItem>
            <CheckCircle style={{ color: '#d9412b', fontSize: 18 }} />
            <StatusText>
              시청 {formatTime(totalWatchTime)}
              {isWatching ? ' · 재생 중' : ''}
            </StatusText>
          </StatusItem>
        </StatusBar>

        {/* 비디오 설명 */}
        <VideoDescription>
          {videoList[nowVideoIndex]?.description || '비디오 설명이 없습니다.'}
        </VideoDescription>

        {/* 비디오 네비게이션 */}
        {videoList.length > 1 && (
          <VideoNavigation>
            <NavButton 
              onClick={handlePrevVideo} 
              disabled={nowVideoIndex === 0}
            >
              <SkipPrevious />
              이전
            </NavButton>
            
            <VideoIndex>
              {nowVideoIndex + 1} / {videoList.length}
            </VideoIndex>
            
            <NavButton 
              onClick={handleNextVideo} 
              disabled={nowVideoIndex === videoList.length - 1}
            >
              다음
              <SkipNext />
            </NavButton>
          </VideoNavigation>
        )}
      </VideoInfo>
    </Card>

    {/* 메모 패널 */}
    {isMemoOpen && (
      <MemoPanel>
        <MemoHeader>
          <MemoTitle>학습 메모</MemoTitle>
          <MemoActions>
            <ModeButton 
              active={memoMode === 'text'} 
              onClick={() => {
                setMemoMode('text');
                fetchMemo();
              }}
            >
              텍스트
            </ModeButton>
            <ModeButton 
              active={memoMode === 'canvas'} 
              onClick={() => {
                setMemoMode('canvas');
                fetchMemo();
              }}
            >
              그리기
            </ModeButton>
            <SaveButton onClick={handleSaveMemo}>
              <Save /> 저장
            </SaveButton>
            <CloseButton onClick={() => setIsMemoOpen(false)}>
              <Close />
            </CloseButton>
          </MemoActions>
        </MemoHeader>
        
        <MemoContent>
          {memoMode === 'text' ? (
            <MemoTextarea
              value={memoText}
              onChange={(e) => setMemoText(e.target.value)}
              placeholder="메모를 입력하세요..."
            />
          ) : (
            <MemoCanvasContainer>
              <MemoToolBar>
                <ToolGroup>
                  <ToolLabel>도구</ToolLabel>
                  <ToolButton onClick={toggleEraser} active={!isErasing}>
                    펜
                  </ToolButton>
                  <ToolButton onClick={toggleEraser} active={isErasing}>
                    지우개
                  </ToolButton>
                </ToolGroup>
                <ToolGroup>
                  <ToolLabel>크기</ToolLabel>
                  <SizeButton onClick={DownToolSize}>−</SizeButton>
                  <SizeDisplay>{isErasing ? eraserSize : penSize}</SizeDisplay>
                  <SizeButton onClick={UpToolSize}>+</SizeButton>
                </ToolGroup>
                <ToolButton onClick={handleClearCanvas} variant="secondary">
                  전체 지우기
                </ToolButton>
              </MemoToolBar>
              <MemoCanvasWrapper>
                <MemoCanvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseUp={stopDrawing}
                  onMouseMove={draw}
                  onMouseLeave={stopDrawing}
                />
              </MemoCanvasWrapper>
            </MemoCanvasContainer>
          )}
        </MemoContent>
      </MemoPanel>
    )}

    {/* 메모 열기 버튼 */}
    <MemoToggleButton onClick={() => setIsMemoOpen(!isMemoOpen)}>
      <Note />
      {isMemoOpen ? '메모 닫기' : '메모'}
    </MemoToggleButton>

    {/* 모달 */}
    <Modal
      isOpen={isOpenModal}
      onClose={modalProps.cancelButtonName ? () => setIsOpenModal(false) : null}
      title={modalProps.title}
      onConfirm={modalProps.onConfirm}
      confirmButtonName={modalProps.confirmButtonName}
      cancelButtonName={modalProps.cancelButtonName}
    >
      {modalProps.message}
    </Modal>
    </>
  );
};

export default LectureVideo; 